import { useMemo, useState } from 'react';
import { Card, CardBody, CardTitle, Button, Alert } from 'reactstrap';
import { SettingsField } from './SettingsField';

/**
 * Settings-Group-Card.
 *
 * Pattern:
 *  - Title + Description oben
 *  - Eine `<SettingsField>` pro setting
 *  - Unten: "Speichern" + "Verwerfen" Footer, scoped auf diese Card
 *  - Save sendet nur ein Diff der geänderten Felder via `onSave(patch)`
 *  - Per-Field "Reset to env-Default" via `onResetField(key)`
 *
 * Per-Card-Save ist absichtlich gewählt statt Global-Save:
 *  - kleinere Patches, geringeres Risiko von Cross-Group-Konflikten
 *  - Operator sieht klar welche Gruppe gespeichert wurde
 *  - Audit-Log-Entries pro Gruppe → bessere Nachvollziehbarkeit
 *
 * Props:
 *  - title: string
 *  - description?: string
 *  - settings: SettingShape[] (siehe SettingsField doc)
 *  - onSave: (patch: Record<string, unknown>) => Promise
 *  - onResetField?: (key: string) => Promise
 *  - busy?: boolean (während Save / Reset)
 *  - writePerms?: string[] (HasPermission-Filter — wenn fehlt, Card readonly)
 */
export function SettingsCard({
  title,
  description,
  settings,
  onSave,
  onResetField,
  busy = false,
  canWrite = true,
}) {
  const safeSettings = useMemo(
    () => (Array.isArray(settings) ? settings : []),
    [settings],
  );
  // Form-State = Map von key → Override-Wert. Nicht gesetzt = nicht geändert.
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);

  const dirty = useMemo(() => {
    const out = {};
    Object.entries(draft).forEach(([k, v]) => {
      const orig = safeSettings.find((s) => s.key === k);
      if (!orig) return;
      // Strikter ===-Vergleich; bei number bedeutet null = "unset"
      if (v !== orig.value) out[k] = v;
    });
    return out;
  }, [draft, safeSettings]);

  const hasChanges = Object.keys(dirty).length > 0;

  const onFieldChange = (key, newValue) => {
    setDraft((prev) => ({ ...prev, [key]: newValue }));
    setError(null);
  };

  const onDiscard = () => {
    setDraft({});
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges) return;
    try {
      await onSave(dirty);
      setDraft({});
    } catch (err) {
      setError(err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  const onResetFieldClick = async (key) => {
    if (!onResetField) return;
    try {
      await onResetField(key);
      setDraft((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      setError(err?.message ?? 'Reset fehlgeschlagen.');
    }
  };

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h5" className="mb-1">{title}</CardTitle>
        {description && <p className="text-muted small mb-3">{description}</p>}

        <form onSubmit={onSubmit}>
          {safeSettings.length === 0 && (
            <Alert color="warning" className="small mb-3">
              Keine Settings in der Response. Vermutlich liefert Core hier eine
              andere Shape als <code>{'{ settings: [...] }'}</code>. Check Console
              für <code>[orbit:settings] raw</code>.
            </Alert>
          )}
          {safeSettings.map((s) => {
            const liveValue = s.key in draft ? draft[s.key] : s.value;
            const isDirty = s.key in dirty;
            return (
              <SettingsField
                key={s.key}
                setting={s}
                value={liveValue}
                onChange={(v) => onFieldChange(s.key, v)}
                onResetToEnvDefault={
                  onResetField && canWrite ? () => onResetFieldClick(s.key) : undefined
                }
                dirty={isDirty}
                disabled={busy || !canWrite}
              />
            );
          })}

          {error && <Alert color="danger" className="mb-3">{error}</Alert>}

          {!canWrite && (
            <Alert color="secondary" className="mb-3 small">
              Nur-Lese-Modus — fehlende Schreib-Permission. Werte sind sichtbar
              aber nicht änderbar.
            </Alert>
          )}

          {canWrite && (
            <div className="d-flex justify-content-end gap-2">
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={onDiscard}
                disabled={!hasChanges || busy}
                type="button"
              >
                Verwerfen
              </Button>
              <Button
                color="primary"
                size="sm"
                disabled={!hasChanges || busy}
                type="submit"
              >
                {busy ? 'Speichern…' : `Speichern${hasChanges ? ` (${Object.keys(dirty).length})` : ''}`}
              </Button>
            </div>
          )}
        </form>
      </CardBody>
    </Card>
  );
}
